import { useEffect, useRef } from "react";
import { useOutletContext, useParams } from "react-router";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import type { CustomerOutletContext } from "@acto/taskpane/field-sales-2/CustomerDetailsLayout";
import { ShellSlotContent } from "@acto/taskpane/field-sales-2/shell";
import { PageLayout } from "@acto/taskpane/field-sales-2/components/patterns/PageLayout";
import { SelectMenu } from "@acto/taskpane/field-sales-2/components/primitives/SelectMenu";
import { kpiTabLabelKey } from "@acto/taskpane/field-sales-2/data-access/buildKpiTrend";
import { useCorporateGroupSummary } from "@acto/taskpane/field-sales-2/data-access/useCorporateGroupSummary";
import { useGroupKpiTrend } from "@acto/taskpane/field-sales-2/data-access/useGroupKpiTrend";
import { useGroupProductGroups } from "@acto/taskpane/field-sales-2/data-access/useGroupProductGroups";
import { AnalysisDrillNav } from "./AnalysisDrillNav";
import { AnalysisNavDock } from "./AnalysisNavDock";
import { GroupMembersView } from "./GroupMembersView";
import { ProductAnalysisView } from "./ProductAnalysisView";
import { useProductDrill } from "./ProductDrillContext";
import { useAnalysisListControls } from "./useAnalysisListControls";
import { CUSTOMERS_OPTION, PRODUCTS_OPTION, useAnalysisBreakdown } from "./useAnalysisBreakdown";

// Matches ListTitle's lead so the toggle title aligns with the card edge in hPadding="card" mode.
const TitleIndent = styled("div")(({ theme }) => ({
  paddingLeft: theme.fieldSalesTwo.spacing[16],
  minWidth: 0,
}));

/**
 * Group-wide KPI card with the member list (default) or the group's product breakdown, scoped to
 * the corporate group of the customer in the route.
 */
export function CorporateGroupAnalysis() {
  const { id } = useParams<{ id: string }>();
  const tAnalysis = useTranslations("fieldSalesTwo.analysis");
  const tGroup = useTranslations("fieldSalesTwo.corporateGroup");
  const tSearch = useTranslations("fieldSalesTwo.search");
  const tKpi = useTranslations("kpi");

  const { dateRange, isCustomRange, selectedKpi } = useOutletContext<CustomerOutletContext>();

  const { group, isPending: isGroupPending } = useCorporateGroupSummary(id);
  const groupTitle = group?.name ?? tGroup("viewTitle");

  const { stack, drillTo } = useProductDrill();
  const currentGroup = stack.at(-1);

  // The drill stack lives in the customer shell's provider and outlives this page; clear it on
  // entry and exit so no drill path leaks between the pages sharing it.
  const drillToRef = useRef(drillTo);
  drillToRef.current = drillTo;
  useEffect(() => {
    drillToRef.current(0);
    return () => drillToRef.current(0);
  }, []);

  const { searchValue, onSearchChange, debouncedSearch, trends, filterSlot, reset } = useAnalysisListControls(
    currentGroup?.id,
  );

  const { mode, switchMode, switchModeFromNavDock, docked, dockSearchOpen, setDockSearchOpen, headerSentinelRef } =
    useAnalysisBreakdown({
      defaultMode: "customers",
      // Leaving the product hierarchy: drop any drill so the toggle starts at the root list.
      onLeaveProducts: () => drillTo(0),
      resetListControls: reset,
    });

  const trend = useGroupKpiTrend({
    clientId: id,
    kpi: selectedKpi,
    dateRange,
    isCustomRange,
    productGroupId: mode === "products" ? currentGroup?.id : undefined,
  });

  const products = useGroupProductGroups({
    clientId: id,
    parent: currentGroup?.id,
    dateRange,
    searchTerm: debouncedSearch,
    selectedKpi,
    trends,
    enabled: mode === "products",
  });

  // Switches in place — no scroll-to-top — since the inline title already sits at the top of the list.
  const breakdownToggle = (
    <TitleIndent>
      <SelectMenu
        variant="sectionTitle"
        options={[
          { value: PRODUCTS_OPTION, label: tAnalysis("productGroups") },
          { value: CUSTOMERS_OPTION, label: tGroup("members") },
        ]}
        value={mode === "products" ? PRODUCTS_OPTION : CUSTOMERS_OPTION}
        onSelect={(value) => switchMode(value === PRODUCTS_OPTION ? "products" : "customers")}
        triggerLabel={tAnalysis("breakdownToggleLabel")}
      />
    </TitleIndent>
  );

  // Page loader is for the first paint only. The trend card and the breakdown lists own their own
  // reload states, so a root-level search reloads the list in place rather than blanking the whole page.
  const isInitialLoad = isGroupPending || (!currentGroup && trend.isPending);

  const metaLabel = group?.externalReference ?? tKpi(kpiTabLabelKey(selectedKpi));

  return (
    <>
      <ShellSlotContent slot="topBarLeading" id="corporateGroupDrillNav">
        <AnalysisDrillNav rootJumpLabel={groupTitle} />
      </ShellSlotContent>
      {/* Root only: a drilled product level shows its own breadcrumb header. */}
      <AnalysisNavDock
        visible={(docked || dockSearchOpen) && !currentGroup}
        mode={mode}
        productsLabel={tAnalysis("productGroups")}
        customersLabel={tGroup("members")}
        toggleLabel={tAnalysis("breakdownToggleLabel")}
        searchPlaceholder={tSearch("placeholder")}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        onModeChange={switchModeFromNavDock}
        searchOpen={dockSearchOpen}
        onSearchOpenChange={setDockSearchOpen}
      />
      <PageLayout hPadding="card" isLoading={isInitialLoad} isEmpty={!group && !isGroupPending}>
        {!id || !group ? null : mode === "products" ? (
          <ProductAnalysisView
            kind="revenue"
            rootName={groupTitle}
            rootExternalReference={metaLabel}
            trend={trend}
            groups={products}
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            titleSlot={currentGroup ? undefined : breakdownToggle}
            headerSentinelRef={headerSentinelRef}
            filterSlot={filterSlot}
          />
        ) : (
          <GroupMembersView
            clientId={id}
            groupTitle={groupTitle}
            metaLabel={metaLabel}
            selectedKpi={selectedKpi}
            trend={trend}
            titleSlot={breakdownToggle}
            dateRange={dateRange}
            searchValue={searchValue}
            debouncedSearch={debouncedSearch}
            onSearchChange={onSearchChange}
            trends={trends}
            filterSlot={filterSlot}
            headerSentinelRef={headerSentinelRef}
          />
        )}
      </PageLayout>
    </>
  );
}
